import {
  Box,
  BoxProps,
  Icon,
  Stack,
  Step,
  StepLabel,
  Stepper as MuiStepper,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import React, { FC } from 'react';

import { CheckIcon } from '../../assets/icons';
import { TypographyWithOverflowHint } from '../TypographyWithOverflowHint';

export type StepperProps = {
  steps: string[];
  activeStep: number;
  onStepClick: (stepIdx: number) => void;
  completedSteps?: number[];
} & BoxProps;

const ProgressLine = styled(Box)<{
  progress: number;
}>(({ theme, progress }) => ({
  position: 'absolute',
  left: 0,
  right: 0,
  bottom: 0,
  height: 2,
  background: theme.palette.divider,
  zIndex: 0,
  '&:after': {
    content: '""',
    position: 'absolute',
    left: 0,
    top: 0,
    height: '100%',
    width: `${progress}%`,
    background: theme.palette.primary.main,
    zIndex: 1,
    transition: 'width 0.3s',
  },
}));

export const Stepper: FC<StepperProps> = ({
  steps,
  activeStep,
  onStepClick,
  completedSteps = [],
  ...props
}) => {
  const progress = ((activeStep + 1) / steps.length) * 100;
  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        paddingLeft: 2,
        paddingRight: 6.5,
        ...props.sx,
      }}
    >
      <ProgressLine progress={progress} />
      <MuiStepper
        activeStep={activeStep}
        alternativeLabel={false}
        sx={{
          background: 'transparent',
          position: 'relative',
          zIndex: 1,
          '& .MuiStepConnector-root': { visibility: 'hidden' },
        }}
      >
        {steps.map((label, idx) => {
          const completed = completedSteps.includes(idx);
          const active = idx === activeStep;

          const getColor = () => {
            if (active) return 'primary.main';
            if (completed) return 'success.main';
            return 'common.grey';
          };
          return (
            <Step key={label} completed={completed}>
              <Stack direction="row" alignItems="center" gap={0.5}>
                <StepLabel
                  StepIconComponent={({ icon }) => (
                    <Typography
                      variant="h3"
                      sx={{
                        color: getColor(),
                        display: 'inline-block',
                      }}
                    >
                      {icon}
                    </Typography>
                  )}
                  onClick={() => {
                    onStepClick(idx);
                  }}
                  sx={{
                    cursor: completed || active ? 'pointer' : 'default',
                    mb: 0.5,
                  }}
                >
                  <Stack direction="row" alignItems="center" gap={0.25}>
                    <TypographyWithOverflowHint
                      variant="h3"
                      sx={{
                        display: 'inline-block',
                        color: getColor(),
                      }}
                    >
                      {label}
                    </TypographyWithOverflowHint>
                  </Stack>
                </StepLabel>
                <Icon
                  sx={{
                    visibility: completed ? 'visible' : 'hidden',
                    color: 'success.main',
                    alignSelf: 'flex-start',
                  }}
                >
                  <CheckIcon />
                </Icon>
              </Stack>
            </Step>
          );
        })}
      </MuiStepper>
    </Box>
  );
};
